import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { createOrganization, getOrganizations } from "../../api/organizations";
import { isPlatformAdminUser } from "../../utils/auth";

const TenantsListPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isPlatformAdminUser()) {
      navigate("/403", { replace: true });
    }
  }, [navigate]);

  const load = async () => {
    setError("");
    try {
      const resp = await getOrganizations(1, 100);
      const data = resp.data;
      setRows(data.results || data || []);
    } catch {
      setError(t("admin.tenantsSaveError"));
      setRows([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const resp = await createOrganization({
        slug: slug.trim().toLowerCase(),
        display_name: displayName.trim(),
        is_carrier: true,
      });
      navigate(`/administrator/tenants/${resp.data.id}`);
    } catch {
      setError(t("admin.tenantsSaveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        component={RouterLink}
        to="/administrator/dashboard"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        {t("header.adminPanel")}
      </Button>
      <Typography variant="h5" fontWeight={700}>
        {t("admin.tenantsTitle")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t("admin.tenantsHint")}
      </Typography>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography fontWeight={600} sx={{ mb: 2 }}>
          {t("admin.tenantsCreate")}
        </Typography>
        <Stack component="form" onSubmit={handleCreate} direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label={t("admin.tenantsSlug")}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            size="small"
          />
          <TextField
            label={t("admin.tenantsDisplayName")}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            size="small"
            sx={{ flex: 1 }}
          />
          <Button type="submit" variant="contained" disabled={saving}>
            {t("admin.tenantsCreate")}
          </Button>
        </Stack>
      </Paper>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("admin.tenantsSlug")}</TableCell>
            <TableCell>{t("admin.tenantsDisplayName")}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>{t("admin.tenantsEmpty")}</TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.slug}</TableCell>
                <TableCell>{row.display_name || row.name}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() => navigate(`/administrator/tenants/${row.id}`)}
                  >
                    {t("admin.tenantsOpen")}
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Container>
  );
};

export default TenantsListPage;
